import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import OrderLookupForm from "./components/OrderLookupForm";
import OrderResult, { type OrderData } from "./components/OrderResult";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const EDGE_URL = `${SUPABASE_URL}/functions/v1/shopify-order-lookup`;

type PageState = "form" | "loading" | "result" | "error";

const trustItems = [
  { icon: "ri-shield-check-line", label: "Secure Lookup" },
  { icon: "ri-lock-password-line", label: "Email Verified" },
  { icon: "ri-customer-service-2-line", label: "Live Support" },
];

export default function OrderTrackingPage() {
  const [pageState, setPageState] = useState<PageState>("form");
  const [order, setOrder] = useState<OrderData | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleLookup = async (orderNumber: string, email: string) => {
    setPageState("loading");
    setErrorMsg("");

    try {
      const res = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderNumber, email }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        setErrorMsg(data.error || "Order not found. Please check your details and try again.");
        setPageState("error");
        return;
      }

      setOrder(data.order);
      setPageState("result");
    } catch {
      setErrorMsg("Connection error. Please check your internet and try again.");
      setPageState("error");
    }
  };

  const handleReset = () => {
    setOrder(null);
    setErrorMsg("");
    setPageState("form");
  };

  return (
    <main className="font-sans bg-white min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gray-900 pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        {/* Background texture */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://public.readdy.ai/ai/img_res/8754806ba7cc9534b7ff3081c0d256c6.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/60 to-gray-900/90" />

        <div className="relative max-w-3xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-truck-line"></i>
            </div>
            Order Tracking
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3 leading-tight">
            Track Your Order
          </h1>
          <p className="text-gray-400 text-base md:text-lg mb-10 max-w-xl mx-auto">
            Enter your order number and the email address you used at checkout to see live status, tracking info, and more.
          </p>

          {/* Lookup form card */}
          <div className="bg-white rounded-2xl p-6 md:p-8 text-left">
            {(pageState === "form" || pageState === "loading") && (
              <OrderLookupForm onSubmit={handleLookup} loading={pageState === "loading"} />
            )}

            {pageState === "error" && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4">
                  <div className="w-5 h-5 flex items-center justify-center text-red-500 shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-red-700 mb-0.5">Order not found</p>
                    <p className="text-sm text-red-600">{errorMsg}</p>
                  </div>
                </div>
                <OrderLookupForm onSubmit={handleLookup} loading={false} />
              </div>
            )}

            {pageState === "result" && order && (
              <OrderResult order={order} onReset={handleReset} />
            )}
          </div>

          {/* Trust row */}
          <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
            {trustItems.map((t) => (
              <div key={t.label} className="flex items-center gap-1.5 text-gray-400 text-xs">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${t.icon} text-amber-400`}></i>
                </div>
                {t.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help section */}
      <section className="max-w-4xl mx-auto px-4 md:px-6 py-16 w-full">
        <h2 className="text-xl font-extrabold text-gray-900 text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            {
              q: "Where do I find my order number?",
              a: "Your order number is in the confirmation email sent right after you placed your order. It looks like #1001.",
              icon: "ri-mail-open-line",
            },
            {
              q: "How long until my order ships?",
              a: "Most orders are processed within 1–2 business days. You'll receive a shipping confirmation email with tracking info once it's on its way.",
              icon: "ri-time-line",
            },
            {
              q: "My order number doesn't work — what do I do?",
              a: "Double-check the email you used at checkout. If the issue persists, contact our support team and we'll look it up manually.",
              icon: "ri-question-answer-line",
            },
            {
              q: "Can I change or cancel my order?",
              a: "Orders can typically be modified or cancelled within 24 hours of placement. Contact us immediately at (866) 605-4975 for fastest service.",
              icon: "ri-edit-box-line",
            },
            {
              q: "What if my package shows delivered but I didn't receive it?",
              a: "Check with neighbors and your building office first. If it's still missing, contact us within 5 days and we'll file a carrier claim.",
              icon: "ri-map-pin-line",
            },
            {
              q: "Do you ship outside the US?",
              a: "Currently we ship to all 48 contiguous states. Contact us for Alaska, Hawaii, or international inquiries.",
              icon: "ri-earth-line",
            },
          ].map((faq) => (
            <div key={faq.q} className="border border-gray-100 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                  <i className={`${faq.icon} text-amber-500 text-base`}></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 mb-1.5">{faq.q}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-10 bg-gray-900 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-xl mx-auto mb-4">
            <i className="ri-customer-service-2-line text-2xl text-amber-400"></i>
          </div>
          <h3 className="text-lg font-extrabold text-white mb-2">Still need help?</h3>
          <p className="text-gray-400 text-sm mb-5">
            Our support team is standing by Mon–Fri 9am–6pm EST.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="tel:8666054975"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-6 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line"></i>
              </div>
              (866) 605-4975
            </a>
            <Link
              to="/contact"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line"></i>
              </div>
              Send a Message
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
